import { shallowMount } from "@vue/test-utils";
import App from "@/App";
import { motorTCCA } from "@/config/brands/ofertaCajaAhorro";

const baseStep = {
  footerText: "Footer Text",
  headerTheme: "light",
  footerTheme: "dark",
  footerLayout: "boxed"
};

const createWrapper = ({
  routeName = "mensajeErrorSolicitudCuenta",
  step = baseStep,
  pasos = {},
  includeRouteStep = true,
  oferta = {
    primerPaquete: {
      tipoPaquete: "01"
    },
    tarjetaVos: false,
    tipoSolicitud: "C"
  },
  isLoading = false,
  isVeridasActive = false,
  dvsiFrameData = { id: "iframe-id" },
  configOverrides = {},
  storeOverrides = {}
} = {}) => {
  const config = {
    isVeridasActive,
    pasos: {
      ...(includeRouteStep ? { [routeName]: step } : {}),
      ...pasos
    },
    ...configOverrides
  };

  const $store = {
    state: {
      isLoading,
      DVSIframeData: dvsiFrameData
    },
    getters: {
      getOferta: jest.fn(() => oferta),
      getRoute: jest.fn(() => ({ name: routeName })),
      ...storeOverrides.getters
    },
    watch: jest.fn(),
    ...storeOverrides
  };

  const wrapper = shallowMount(App, {
    stubs: [
      "router-view",
      "top-bar",
      "app-footer",
      "legales",
      "lottie-loader",
      "embed-veridas",
      "notification-toast",
      "wrap-container"
    ],
    mocks: {
      config,
      $store
    }
  });

  wrapper.vm.vista = routeName;

  if ($store.watch.mock.calls.length > 0) {
    const routeWatcher = $store.watch.mock.calls[0][1];
    routeWatcher({ name: routeName });
  }

  return { wrapper, config, $store };
};

describe("App component", () => {
  beforeEach(() => {
    global._BBVA_MOTOR = "ofertaNomina";
  });

  afterEach(() => {
    global._BBVA_MOTOR = "ofertaNomina";
    delete motorTCCA["01"].consumo;
    delete motorTCCA["09"].consumo;
  });

  it("renders and registers the route watcher", () => {
    const { wrapper, $store } = createWrapper();

    expect(wrapper.vm).toBeDefined();
    expect($store.watch).toHaveBeenCalledWith(
      expect.any(Function),
      expect.any(Function),
      { immediate: true }
    );

    const routeWatcher = $store.watch.mock.calls[0][1];
    routeWatcher({ name: "consulta" });

    expect(wrapper.vm.vista).toBe("consulta");
  });

  it("closeModal calls hideVeridasSdk", () => {
    const { wrapper } = createWrapper();

    wrapper.vm.hideVeridasSdk = jest.fn();

    wrapper.vm.closeModal();

    expect(wrapper.vm.hideVeridasSdk).toHaveBeenCalled();
  });

  it("returns static footer text and computed layout values", () => {
    const { wrapper } = createWrapper();

    expect(wrapper.vm.footerText).toBe("Footer Text");
    expect(wrapper.vm.loading).toBe(false);
    expect(wrapper.vm.canShowFooter).toBe("Footer Text");
    expect(wrapper.vm.headerTheme).toBe("light");
    expect(wrapper.vm.footerTheme).toBe("dark");
    expect(wrapper.vm.footerLayout).toBe("boxed");
    expect(wrapper.vm.inertMainContent).toBeUndefined();
  });

  it("returns null footer text when the route is not configured", () => {
    const { wrapper } = createWrapper({
      routeName: "rutaInexistente",
      includeRouteStep: false,
      pasos: {}
    });

    expect(wrapper.vm.footerText).toBeNull();
    expect(wrapper.vm.canShowFooter).toBe(false);
    expect(wrapper.vm.headerTheme).toBe("dark");
    expect(wrapper.vm.footerTheme).toBeNull();
    expect(wrapper.vm.footerLayout).toBeNull();
  });

  it("removes the package block for tipoPaquete 09", () => {
    global._BBVA_MOTOR = "ofertaCajaAhorro";

    const { wrapper } = createWrapper({
      routeName: "ofertaCA",
      step: {
        footerText: {
          cajaAhorro: "CA",
          paquetes: "Antes $sin_consumo texto intermedio $sin_consumo Despues"
        }
      },
      oferta: {
        primerPaquete: {
          tipoPaquete: "09"
        },
        tarjetaVos: false,
        tipoSolicitud: "C"
      }
    });

    expect(wrapper.vm.footerText).toBe("CA<br /><br />Antes  Despues");
  });

  it("replaces consumo and clears $sin_consumo for other package types", () => {
    global._BBVA_MOTOR = "ofertaCajaAhorro";
    motorTCCA["01"].consumo = 55000;

    const { wrapper } = createWrapper({
      routeName: "ofertaCA",
      step: {
        footerText: {
          cajaAhorro: "CA",
          paquetes: "Monto $consumo y marca $sin_consumo fin $sin_consumo"
        }
      },
      oferta: {
        primerPaquete: {
          tipoPaquete: "01"
        },
        tarjetaVos: false,
        tipoSolicitud: "C"
      }
    });

    expect(wrapper.vm.footerText).toContain("$55.000");
    expect(wrapper.vm.footerText).not.toContain("$consumo");
    expect(wrapper.vm.footerText).not.toContain("$sin_consumo");
  });

  it("appends tarjetaVos text when the offer is not a TC package", () => {
    global._BBVA_MOTOR = "ofertaCajaAhorro";

    const { wrapper } = createWrapper({
      routeName: "ofertaCA",
      step: {
        footerText: {
          cajaAhorro: "CA",
          tarjetaVos: "TARJETA VOS"
        }
      },
      oferta: {
        primerPaquete: {
          tipoPaquete: "XX"
        },
        tarjetaVos: true,
        tipoSolicitud: "C"
      }
    });

    expect(wrapper.vm.footerText).toBe("CA<br /><br />TARJETA VOS");
  });

  it("returns an empty footer text for caja ahorro in vista V", () => {
    global._BBVA_MOTOR = "ofertaCajaAhorro";

    const { wrapper } = createWrapper({
      routeName: "ofertaCA",
      step: {
        footerText: {
          cajaAhorro: "CA",
          paquetes: "Paquetes"
        }
      },
      oferta: {
        primerPaquete: {
          tipoPaquete: "01"
        },
        tarjetaVos: false,
        tipoSolicitud: "V"
      }
    });

    expect(wrapper.vm.footerText).toBe("Paquetes");
    expect(wrapper.vm.canShowFooter).toBe("Paquetes");
  });

  it("renders footer related stubs when the route is oferta", async () => {
    const { wrapper } = createWrapper({
      routeName: "oferta",
      step: {
        footerText: "Footer oferta",
        headerTheme: "light",
        footerTheme: "dark",
        footerLayout: "boxed"
      },
      oferta: {
        primerPaquete: {
          tipoPaquete: "01"
        },
        tarjetaVos: false,
        tipoSolicitud: "C"
      }
    });

    wrapper.setData({ inIframe: false });
    await wrapper.vm.$nextTick();

    expect(wrapper.html()).toContain("top-bar-stub");
    expect(wrapper.html()).toContain("legales-stub");

    wrapper.setData({ inIframe: true });
    await wrapper.vm.$nextTick();

    expect(wrapper.html()).not.toContain("top-bar-stub");
  });
});
